import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Table,
  Tooltip,
  message,
  Spin,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { getProjectGroupDetail, getProjectList } from '../../../services/project';
import type { ProjectGroup, Project } from '../../../types/project';
import { useAuth } from '../../../hooks/useAuth';

const { Title, Text } = Typography;

const ProjectGroupDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const [projectGroup, setProjectGroup] = useState<ProjectGroup | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // 获取项目组详情
  const loadProjectGroupDetail = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await getProjectGroupDetail(parseInt(id));
      setProjectGroup(response.data);
      console.log('📋 项目组详情:', response.data);
    } catch (error) {
      console.error('获取项目组详情失败:', error);
      message.error('获取项目组详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取项目组包含的所有项目
  const loadProjects = async () => {
    if (!id) return;
    
    try {
      setProjectsLoading(true);
      const response = await getProjectList({ 
        page_size: 1000,
        project_group: parseInt(id)
      });
      setProjects(response.data.results);
      console.log('📋 项目组包含的项目:', response.data.results);
    } catch (error) {
      console.error('获取项目列表失败:', error);
      message.error('获取项目列表失败');
    } finally {
      setProjectsLoading(false);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    if (id) {
      loadProjectGroupDetail();
      loadProjects();
    }
  }, [id]);

  // 返回列表页
  const handleBackToList = () => {
    // 检查是否有返回状态
    const urlParams = new URLSearchParams(location.search);
    const returnState = urlParams.get('return_state');
    
    if (returnState) {
      try {
        const decodedState = JSON.parse(atob(returnState));
        navigate('/dashboard/project-groups/list', { state: decodedState });
      } catch (error) {
        console.error('解析返回状态失败:', error);
        navigate('/dashboard/project-groups/list');
      }
    } else {
      navigate('/dashboard/project-groups/list');
    }
  };

  // 编辑项目组
  const handleEdit = () => {
    navigate(`/dashboard/project-groups/${id}/edit`);
  };

  // 删除项目组
  const handleDelete = async () => {
    // TODO: 实现删除项目组的逻辑
    message.info('删除功能待实现');
  };

  // 查看项目详情
  const handleViewProject = (projectId: number) => {
    navigate(`/dashboard/projects/${projectId}`);
  };

  // 创建新项目
  const handleCreateProject = () => {
    navigate('/dashboard/projects/create', { 
      state: { project_group: parseInt(id!) }
    });
  };

  // 项目表格列定义
  const projectColumns: ColumnsType<Project> = [
    {
      title: '项目编码',
      dataIndex: 'project_code',
      key: 'project_code',
      render: (text: string, record) => (
        <a onClick={() => handleViewProject(record.id)}>
          {text}
        </a>
      ),
    },
    {
      title: '项目名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => text || '未命名项目',
    },
    {
      title: '项目类型',
      dataIndex: 'project_type_display',
      key: 'project_type',
      render: (text: string) => (
        <Tag color="blue">{text}</Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: {
        showTitle: false,
      },
      render: (text: string) => (
        <Tooltip placement="topLeft" title={text}>
          {text || '-'}
        </Tooltip>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewProject(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>加载项目组详情中...</div>
      </div>
    );
  }

  if (!projectGroup) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Text type="danger">项目组不存在或已被删除</Text>
        <div style={{ marginTop: 16 }}>
          <Button onClick={handleBackToList}>返回列表</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-group-detail">
      {/* 页面头部 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBackToList}
            >
              返回列表
            </Button>
            <Title level={4} style={{ margin: 0 }}>
              项目组详情
            </Title>
          </div>
          
          {/* 操作按钮 */}
          <Space>
            {user?.role === 'customer' && projectGroup.owner.id === user.id && (
              <>
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={handleEdit}
                >
                  编辑项目组
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleDelete}
                >
                  删除项目组
                </Button>
              </>
            )}
          </Space>
        </div>
      </Card>

      {/* 基本信息 */}
      <Card title="基本信息" style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label="项目组名称">
            <Text strong>{projectGroup.name}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="创建者">
            <Tag color="blue">{projectGroup.owner.username}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(projectGroup.created_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {new Date(projectGroup.updated_at).toLocaleString('zh-CN')}
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            {projectGroup.remarks || '无备注'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 所含项目列表 */}
      <Card 
        title={
          <Space>
            <span>所含项目</span>
            <Tag color="blue">{projects.length} 个项目</Tag>
          </Space>
        }
        extra={
          <Space>
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={handleCreateProject}
              style={{
                backgroundColor: '#f6ffed',
                borderColor: '#b7eb8f',
                color: '#52c41a'
              }}
            >
              创建并添加新项目到项目组
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => message.info('添加已有项目功能待实现')}
            >
              添加已有项目到项目组
            </Button>
          </Space>
        }
      >
        {projectsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
            <div style={{ marginTop: 8 }}>加载项目列表中...</div>
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <div>该项目组暂无项目</div>
            <div style={{ marginTop: 8 }}>
              <Button type="primary" onClick={handleCreateProject}>
                创建第一个项目
              </Button>
            </div>
          </div>
        ) : (
          <Table
            columns={projectColumns}
            dataSource={projects}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>
    </div>
  );
};

export default ProjectGroupDetail;
